/**
 * Was ein Messwert-Diagramm vom Server anfordert und was es daraus macht (#738).
 *
 * Ein Diagramm ist 400 Pixel breit; mehr Punkte als Pixel sieht niemand. Ein weiter
 * Zeitraum brachte trotzdem jede Rohmessung einzeln, für eine Geräteklasse mit
 * fünfzehn Kennwerten also fünfzehn solcher Antworten — das war das „sehr langsam,
 * sobald der Zeitraum erweitert wird". Mit `maxPoints` faltet der Server den Zeitraum
 * in höchstens so viele Zeitfenster (Mittelwert je Fenster); unterhalb der Schwelle
 * kommen die Rohwerte unverändert.
 */

/** Höchstzahl der Punkte je Diagramm; darüber liefert der Server Zeitfenster. */
export const MAX_CHART_POINTS = 1000;

export interface ChartSeries {
    name: string;
    data: Array<[number, number]>;
}

/**
 * Die Schlüssel, unter denen die Messungen eines Geräts liegen.
 *
 * `Measurement.asset` ist je nach Schreiber die Mongo-`_id` oder die fachliche `id`
 * (Seriennummer): Das Portal und ältere Server-Pfade schreiben die `_id`, die App und
 * die Inbetriebnahme die fachliche `id`, und was das Gateway meldet, kommt so an, wie es
 * gesendet wurde. Wer nur einen der beiden liest, sieht die Hälfte der Historie nicht —
 * ein Diagramm bleibt dann leer, obwohl das Gerät meldet (tri-server #1016, #738).
 */
export const measurementAssetKeys = (asset: { _id?: unknown; id?: unknown } | null | undefined): string[] => {
    const keys = [asset?._id, asset?.id]
        .filter((key) => key != null && key !== '')
        .map((key) => String(key));
    return Array.from(new Set(keys));
};

const assetFilter = (assetKeys: string | string[]) => {
    const keys = Array.isArray(assetKeys) ? assetKeys : [assetKeys];
    return keys.length === 1 ? keys[0] : { $in: keys };
};

/** Die Anfrage für eine Kennwert-Reihe eines Geräts im angezeigten Zeitraum. */
export const telemetryChartQuery = (
    metric: string,
    assetKeys: string | string[],
    window: { start: Date; end: Date },
) => ({
    metric,
    asset: assetFilter(assetKeys),
    timestamp: { $gte: window.start, $lte: window.end },
    maxPoints: MAX_CHART_POINTS,
});

/** Die Anfrage für die Ereignisse, die ein Diagramm annotieren — ungefaltet, jedes Vorkommen zählt. */
export const telemetryEventQuery = (
    event: string,
    assetKeys: string | string[],
    window: { start: Date; end: Date },
) => ({
    metric: event,
    asset: assetFilter(assetKeys),
    timestamp: { $gte: window.start, $lte: window.end },
});

/** Messungen (roh oder als Zeitfenster) in die Punktfolge des Diagramms. */
export const toChartSeries = (metric: string, measurements: any[]): ChartSeries => ({
    name: metric,
    data: (Array.isArray(measurements) ? measurements : [])
        .filter((measurement) => measurement != null && measurement.timestamp != null)
        .map((measurement) => [new Date(measurement.timestamp).getTime(), measurement.value] as [number, number]),
});

/** Ob es für den Kennwert im Zeitraum überhaupt etwas zu zeichnen gibt. */
export const hasChartData = (series: ChartSeries | null | undefined): boolean =>
    Array.isArray(series?.data) && series!.data.length > 0;

/**
 * Splits the metrics of a device type into those with something to draw in the period and those
 * without (tri-server #1335). The device-type catalogue lists every metric a device could send;
 * field devices that never send e.g. interval min/max would otherwise leave a row of empty charts.
 * Metrics without chart options are dropped entirely, as before.
 */
export const partitionChartMetrics = (
    seriesByMetric: Record<string, ChartSeries | null | undefined>,
    optionsByMetric: Record<string, unknown>,
): { withData: string[]; withoutData: string[] } => {
    const withData: string[] = [];
    const withoutData: string[] = [];

    for (const metric of Object.keys(seriesByMetric)) {
        if (!optionsByMetric[metric] || !seriesByMetric[metric]) {
            continue;
        }

        (hasChartData(seriesByMetric[metric]) ? withData : withoutData).push(metric);
    }

    return { withData, withoutData };
};

/**
 * Lebensphase eines Messwerts von vor der Inbetriebnahme (tri-server #1220): Die
 * Kommissionierung behält die Werte der Werksaktivierung und der Inbetriebnahme vor Ort und
 * markiert sie mit `lifecyclePhase`; spätere Werte tragen kein Feld (= Betrieb). Der Server
 * hält die Phase auch beim Falten (`maxPoints`) auseinander. Die Diagramme zeigen diese Werte
 * standardmäßig, aber markiert — „ist zwischen Produktion und Installation etwas passiert?“
 */
export type LifecyclePhase = 'factory' | 'commissioning';

export interface LifecyclePhaseRange {
    phase: LifecyclePhase;
    from: number;
    to: number;
}

/** Konkrete Hex-Farben: ApexCharts kann keine CSS-Variablen parsen (siehe AssetProperties). */
export const LIFECYCLE_PHASE_COLORS: Record<LifecyclePhase, string> = {
    factory: '#B7791F',
    commissioning: '#2B6CB0',
};

const asLifecyclePhase = (value: unknown): LifecyclePhase | undefined =>
    value === 'factory' || value === 'commissioning' ? value : undefined;

/**
 * Die zusammenhängenden Werks- und Inbetriebnahme-Abschnitte einer Reihe, zeitlich geordnet.
 * Ein Abschnitt reicht bis zum ersten Wert danach — so lange galt sein Wert —, der letzte
 * Abschnitt einer Reihe bis zu seinem letzten Wert.
 */
export const lifecyclePhaseRanges = (measurements: any[]): LifecyclePhaseRange[] => {
    const points = (Array.isArray(measurements) ? measurements : [])
        .filter((measurement) => measurement != null && measurement.timestamp != null)
        .map((measurement) => ({
            time: new Date(measurement.timestamp).getTime(),
            phase: asLifecyclePhase(measurement.lifecyclePhase),
        }))
        .filter((point) => Number.isFinite(point.time))
        .sort((a, b) => a.time - b.time);

    const ranges: LifecyclePhaseRange[] = [];
    let open: LifecyclePhaseRange | null = null;
    for (const point of points) {
        if (open && point.phase === open.phase) {
            open.to = point.time;
            continue;
        }
        if (open) {
            open.to = point.time;
            ranges.push(open);
            open = null;
        }
        if (point.phase) open = { phase: point.phase, from: point.time, to: point.time };
    }
    if (open) ranges.push(open);
    return ranges;
};

/** Die Abschnitte als schattierte x-Achsen-Bereiche mit Beschriftung („Werk“, „Inbetriebnahme“). */
export const lifecyclePhaseAnnotations = (
    ranges: LifecyclePhaseRange[],
    labels: Record<LifecyclePhase, string>,
) =>
    ranges.map((range) => {
        const color = LIFECYCLE_PHASE_COLORS[range.phase];
        return {
            x: range.from,
            x2: range.to,
            fillColor: color,
            opacity: 0.15,
            borderColor: color,
            strokeDashArray: 4,
            label: {
                text: labels[range.phase],
                orientation: 'horizontal',
                position: 'top',
                borderColor: color,
                style: { color: '#fff', background: color },
            },
        };
    });

/** Zählerstand je Medium (tri-model MeasurementType.meterReading*). */
export const METER_READING_METRIC: Record<string, { metric: string; unit: string }> = {
    electricity: { metric: "meterReadingElectricity", unit: "kWh" },
    water: { metric: "meterReadingWater", unit: "m³" },
    gas: { metric: "meterReadingGas", unit: "m³" },
    heat: { metric: "meterReadingHeat", unit: "kWh" },
    cold: { metric: "meterReadingCold", unit: "kWh" },
    compressedAir: { metric: "meterReadingCompressedAir", unit: "m³" },
};

/**
 * Ein Zähler ohne IoT-Gerätetyp (abgelesen, per Excel eingespielt) zeigt im Reiter „Messungen“ seine
 * Rohdaten wie ein Gerät: den Zählerstand. Aggregiert (Verbrauch, Lastgang) wird im Energie-Dashboard,
 * nicht hier.
 */
export const meterDeviceType = (medium: string | null | undefined) => {
    const reading = medium ? METER_READING_METRIC[medium] : undefined;
    return reading ? { id: `meter/${medium}`, state: [{ id: reading.metric, type: "decimal", unit: reading.unit }], events: [] } : null;
};
